import { ReactNode, useMemo } from "react";
import {
  Mutation,
  MutationCache,
  QueryClient,
  QueryClientProvider,
} from "react-query";
import { useSnackbar } from "../components/SnackbarProvider";
import { sentenceCase } from "../util/strings";
import { useAppEventDispatch } from "./useAppEvents";

export function queryRetryPolicy(
  failureCount: number,
  error: { status?: number }
) {
  if (
    typeof error?.status === "number" &&
    error.status >= 400 &&
    error.status < 500 &&
    error.status != 429
  ) {
    // No point in retrying request errors like 404, etc.
    // Fail fast instead.
    return false;
  }
  // Default retry policy: 3 retries, 4 total attempts.
  // Same as the ReactQuery default.
  return failureCount < 3;
}

export const defaultQueryClientParams = {
  defaultOptions: {
    queries: {
      // Don't refetch cached data if it's less than 2 seconds old.
      // By default React Query considers all cached data as stale,
      // and shows the stale data while refetching. This is a sane,
      // but somewhat aggressive default. We increase the timeout
      // to 2 seconds to play nice with pre-fetching on link hover,
      // and avoid the extra API call.
      staleTime: 2_000,
      retry: queryRetryPolicy,
    },
  },
};

/**
 * QueryMetadata describes the user action corresponding to a given
 * query implementation. If defined, this information is passed to
 * the global onSuccess & onError handlers for each invocation of
 * the query. These handlers use the metadata to generate user
 * facing notifications.
 *
 * Centralizing the notifications frees components from the need
 * to depend on the snackbar hooks, or explicitly await mutation
 * results, inspect errors, and show notifications. It also keeps
 * the query layer abstracted from the specific concers of the view
 * layer. I.e. the queries themselves also do not depened on the
 * snackbar provider and display UI components themselves.
 */
export type QueryMetadata = {
  action: Action;
  resourceType: string;
};

export type Actions = typeof actions;
export type ActionId = keyof Actions;
export type Action = Actions[ActionId];

export const actions = {
  create: {
    infinitive: "create",
    continuousTense: "creating",
    pastTense: "created",
  },
  read: {
    infinitive: "read",
    continuousTense: "reading",
    pastTense: "read",
  },
  update: {
    infinitive: "update",
    continuousTense: "updating",
    pastTense: "updated",
  },
  delete: {
    infinitive: "delete",
    continuousTense: "deleting",
    pastTense: "deleted",
  },
  set: {
    infinitive: "set",
    continuousTense: "setting",
    pastTense: "set",
  },
  request: {
    infinitive: "request",
    continuousTense: "requesting",
    pastTense: "requested",
  },
} as const;

export function mutationSuccessHandler({
  notify,
}: {
  notify: ReturnType<typeof useSnackbar>;
}) {
  return (
    _data: unknown,
    _variables: unknown,
    _context: any,
    mutation: Mutation<unknown, unknown, unknown, unknown>
  ) => {
    const action: Partial<Action> = mutation.meta?.action;
    const resourceType = mutation.meta?.resourceType;

    if (!action) {
      // Do nothing if there is no action metadata.
      // This prevents showing duplicate notifications for
      // legacy queries that don't have meta defined, and
      // do their own notifications.
      return;
    }

    if (action?.infinitive === "read") {
      // Ignore read operations
      return;
    }

    let title = "Success";

    if (action?.pastTense && resourceType) {
      switch (action.infinitive) {
        case "create": {
          title = `New ${resourceType} ${action.pastTense}`;
          break;
        }
        case "update":
        case "request":
        case "delete": {
          title = `${action.pastTense} ${resourceType}`;
          break;
        }
        case "set": {
          title = `${resourceType} was ${action.pastTense} successfully`;
          break;
        }
      }
    }

    notify({
      title: sentenceCase(title),
      message: undefined,
      severity: "success",
    });
  };
}

export function mutationErrorHandler({ notify }) {
  return (
    error: any,
    _variables: unknown,
    _context: any,
    mutation: Mutation<unknown, unknown, unknown, unknown>
  ) => {
    const action: Partial<Action> = mutation.meta?.action;
    const resourceType = mutation.meta?.resourceType;

    if (!action) {
      // Do nothing if there is no action metadata.
      // This prevents showing duplicate notifications for
      // legacy queries that don't have meta defined, and
      // do their own notifications.
      return;
    }

    let title =
      action?.continuousTense && resourceType
        ? `Error ${action.continuousTense} ${resourceType}`
        : "Error";

    notify({
      title,
      message: error?.message ?? undefined,
      severity: "error",
    });
  };
}

/**
 * Constructs a new query client for handling queries.
 *
 * The client is configured a custom retry policy and centralized
 * error handling for queries and mutations.
 *
 * Clients that simply want access to the query client in context,
 * such as custom hooks that define queries and mutations or interact
 * with the query cache, should call `useQueryClient` from react-query
 * directly.
 */
export function useNewQueryClient() {
  const notify = useSnackbar();
  const dispatchEvent = useAppEventDispatch();

  return useMemo(() => {
    const queryClient = new QueryClient({
      ...defaultQueryClientParams,
      mutationCache: new MutationCache({
        onSuccess: (data, variables, context, mutation) => {
          queryClient.invalidateQueries();
          dispatchEvent("mutation:success", {
            key: mutation.options.mutationKey,
            data,
            variables,
            context,
          });
          mutationSuccessHandler({ notify })(
            data,
            variables,
            context,
            mutation
          );
        },
        onError: (error, variables, context, mutation) => {
          dispatchEvent("mutation:error", {
            key: mutation.options.mutationKey,
            error,
            variables,
            context,
          });
          mutationErrorHandler({ notify })(error, variables, context, mutation);
        },
      }),
    });
    return queryClient;
  }, []);
}

export function AppQueryClientProvider({ children }: { children: ReactNode }) {
  const client = useNewQueryClient();
  return <QueryClientProvider client={client}>{children}</QueryClientProvider>;
}
